import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';

import { MpsUser } from '../../app/models/mps-user.model';
import { MpsApiService } from './mps-api.service';
import { endpoints } from '../mps-endpoints';
import { MpsILaravelDefaultResponse } from '../interfaces/mps-responses.interface';
import { MpsOauth2Service } from './mps-oauth2.service';
import { MpsOauth2Provider } from '../models/mps-provider.model';
import { mpsRouteNames } from '../mps-route-names';

@Injectable({
  providedIn: 'root',
})
export class MpsUserService {
  private _userSubject = new BehaviorSubject<MpsUser | null>(null);
  private _apiService = inject(MpsApiService);
  private _oauth2Service = inject(MpsOauth2Service);
  private _router = inject(Router);

  public changeAffiliate(affiliateId: number): Observable<MpsUser> {
    return this._apiService
      .post<{ affiliate_id: number }, MpsILaravelDefaultResponse<MpsUser>>(
        endpoints.changeAffiliate,
        { affiliate_id: affiliateId }
      )
      .pipe(
        map((resp: { data: MpsUser }) => {
          const user = new MpsUser(resp.data);
          this._userSubject.next(user);
          return user;
        })
      );
  }

  public fetchUser(): Observable<MpsUser> {
    return this._apiService.get<{ data: MpsUser }>(endpoints.user).pipe(
      catchError((error) => {
        if (error.status === 401) {
          const provider = localStorage.getItem('provider');

          if (!provider) {
            this.getProviders();
          } else {
            return this.refreshToken(provider);
          }
        }
        return throwError(() => error);
      }),
      map((resp: { data: MpsUser }) => {
        const user = new MpsUser(resp.data);
        this._userSubject.next(user);
        localStorage.setItem('lang', user.lang);
        return user;
      })
    );
  }

  public logout(): Observable<MpsILaravelDefaultResponse<boolean>> {
    return this._apiService.get<MpsILaravelDefaultResponse<boolean>>(endpoints.logout).pipe(
      tap(() => {
        this._userSubject.next(null);
        localStorage.removeItem('token');
      })
    );
  }

  public isLoggedIn(): boolean {
    return this.getUserSubject().value !== null;
  }

  public getUserSubject(): BehaviorSubject<MpsUser | null> {
    return this._userSubject;
  }

  public getUser(): MpsUser | null {
    return this._userSubject.value;
  }

  public changeLang(userId: number, lang: string): Observable<MpsUser> {
    return this._apiService
      .post<{ lang: string }, MpsILaravelDefaultResponse<MpsUser>>(
        endpoints.changeLang.replace(':userId', userId.toString()),
        { lang: lang }
      )
      .pipe(
        map((resp: { data: MpsUser }) => {
          const user = new MpsUser(resp.data);
          this._userSubject.next(user);
          return user;
        })
      );
  }

  private refreshToken(provider: string): Observable<{ data: MpsUser }> {
    return this._oauth2Service.refreshToken(provider).pipe(
      switchMap((response) => {
        localStorage.setItem('token', response.token);

        return this._apiService.get<{ data: MpsUser }>(endpoints.user);
      }),
      catchError((error: HttpErrorResponse) => {
        this.getProviders();
        return throwError(() => error);
      })
    );
  }

  private getProviders(): void {
    this._oauth2Service.getProviders().subscribe((providers: MpsOauth2Provider[]) => {
      if (providers.length === 1) {
        localStorage.setItem('provider', providers[0].code);
        window.location.href = providers[0].url;
      } else {
        this._router.navigateByUrl(mpsRouteNames.auth + '/' + mpsRouteNames.login);
      }
    });
  }
}
